import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { transactions } from '../services/api';
import { Transaction, TransactionFilters, PaginationInfo } from '../types';
import { useBudget } from './BudgetContext';
import type { TransactionChange } from './NavigationContext';
import { useNavigation } from './NavigationContext';

interface TransactionContextType {
  // Current page transactions
  transactions: Transaction[];
  // Recent transactions (for dashboard)
  recentTransactions: Transaction[];
  // Pagination info
  pagination: PaginationInfo | null;
  // Loading states
  loading: boolean;
  loadingMore: boolean;
  // Filters
  filters: TransactionFilters;
  setFilters: (filters: TransactionFilters) => void;
  // Category filter (for dashboard filtering)
  categoryFilter: string | null;
  setCategoryFilter: (category: string | null) => void;
  // Pagination controls
  pageSize: number;
  setPageSize: (size: number) => void;
  loadPage: (page: number, skipBudgetFilter?: boolean) => Promise<void>;
  loadMoreTransactions: (skipBudgetFilter?: boolean) => Promise<void>;
  // Other functions
  handleUpdateTransactionCategory: (transaction: Transaction, newCategory: string) => Promise<void>;
  refreshTransactions: (skipBudgetFilter?: boolean) => Promise<void>;
  // All transactions for filtering (when needed)
  allTransactions: Transaction[];
  getAllTransactions: (skipBudgetFilter?: boolean) => Promise<void>;
}

export const TransactionContext = createContext<TransactionContextType>({
  transactions: [],
  recentTransactions: [],
  pagination: null,
  loading: false,
  loadingMore: false,
  filters: {},
  setFilters: () => {},
  categoryFilter: null,
  setCategoryFilter: () => {},
  pageSize: 50,
  setPageSize: () => {},
  loadPage: async () => {},
  loadMoreTransactions: async () => {},
  handleUpdateTransactionCategory: async () => {},
  refreshTransactions: async () => {},
  allTransactions: [],
  getAllTransactions: async () => {}
});

export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
};

export const TransactionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentBudget, setCurrentBudget } = useBudget();
  const { setChangeHistory, setUndoneChanges } = useNavigation();
  
  // State for paginated transactions
  const [currentTransactions, setCurrentTransactions] = useState<Transaction[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updatingTransaction, setUpdatingTransaction] = useState(false);
  
  // State for filters and pagination controls
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [pageSize, setPageSize] = useState(50);
  
  // State for category filter (used by dashboard)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  
  // State for all transactions (used for filtering across all data)
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);

  // Computed filtered transactions based on current filters
  const filteredCurrentTransactions = React.useMemo(() => {
    if (Object.keys(filters).length === 0) {
      return currentTransactions;
    }

    let result = [...currentTransactions];

    // Apply filters instantly to current transactions
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      result = result.filter(t => new Date(t.date) >= startDate);
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      result = result.filter(t => new Date(t.date) <= endDate);
    }
    if (filters.description) {
      const searchTerm = filters.description.toLowerCase();
      result = result.filter(t => t.description.toLowerCase().includes(searchTerm));
    }
    if (filters.category) {
      const searchTerm = filters.category.toLowerCase();
      result = result.filter(t => t.category.toLowerCase().includes(searchTerm));
    }
    if (filters.type) {
      result = result.filter(t => t.type === filters.type);
    }
    if (filters.minAmount) {
      const minAmount = parseFloat(filters.minAmount);
      result = result.filter(t => t.amount >= minAmount);
    }
    if (filters.maxAmount) {
      const maxAmount = parseFloat(filters.maxAmount);
      result = result.filter(t => t.amount <= maxAmount);
    }

    return result;
  }, [currentTransactions, filters]);

  // Computed recent transactions (filtered by category if set)
  const recentTransactions = React.useMemo(() => {
    let transactions = filteredCurrentTransactions;
    
    if (categoryFilter) {
      transactions = transactions.filter(t => t.category === categoryFilter);
    }
    
    // Return up to 15 recent transactions for dashboard
    return transactions.slice(0, 15);
  }, [filteredCurrentTransactions, categoryFilter]);

  // Function to load a specific page
  const loadPage = useCallback(async (page: number, skipBudgetFilter = false) => {
    // Prevent duplicate calls
    if (loadInProgress.current) {
      return;
    }
    
    try {
      loadInProgress.current = true;
      setLoading(true);
      
      let queryFilters = { ...filters, page, limit: pageSize };
      
      // If we have a current budget and we're not skipping budget filter, filter by its month
      // BUT only if the user hasn't set their own date filters
      if (currentBudget && !skipBudgetFilter && !filters.startDate && !filters.endDate) {
        const budgetDate = new Date(currentBudget.month);
        const year = budgetDate.getFullYear();
        const month = budgetDate.getMonth();
        
        const startOfMonth = new Date(year, month, 1);
        startOfMonth.setUTCHours(0, 0, 0, 0);
        
        const endOfMonth = new Date(year, month + 1, 0);
        endOfMonth.setUTCHours(23, 59, 59, 999);
        
        queryFilters = {
          ...queryFilters,
          startDate: startOfMonth.toISOString(),
          endDate: endOfMonth.toISOString()
        };
      }

      const response = await transactions.getAll(queryFilters);
      setCurrentTransactions(response.transactions);
      setPaginationInfo(response.pagination);
    } catch (err) {
      console.error('Error loading transactions page:', err);
    } finally {
      setLoading(false);
      loadInProgress.current = false;
    }
  }, [filters, pageSize, currentBudget]);

  // Function to load more data for filtering (when we need more transactions to filter from)
  const loadMoreDataForFiltering = useCallback(async () => {
    if (!paginationInfo || !paginationInfo.hasNextPage || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      let queryFilters: any = { page: paginationInfo.currentPage + 1, limit: pageSize };
      
      // Don't apply any filters when loading more data - we want raw data to filter client-side
      if (currentBudget && !filters.startDate && !filters.endDate) {
        const budgetDate = new Date(currentBudget.month);
        const year = budgetDate.getFullYear();
        const month = budgetDate.getMonth();
        
        const startOfMonth = new Date(year, month, 1);
        startOfMonth.setUTCHours(0, 0, 0, 0);
        
        const endOfMonth = new Date(year, month + 1, 0);
        endOfMonth.setUTCHours(23, 59, 59, 999);
        
        queryFilters = {
          ...queryFilters,
          startDate: startOfMonth.toISOString(),
          endDate: endOfMonth.toISOString()
        };
      }

      const response = await transactions.getAll(queryFilters);
      setCurrentTransactions(prev => [...prev, ...response.transactions]);
      setPaginationInfo(response.pagination);
    } catch (err) {
      console.error('Error loading more data for filtering:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [pageSize, currentBudget, filters.startDate, filters.endDate, paginationInfo, loadingMore]);

  // Function to load more transactions (append to current list)
  const loadMoreTransactions = useCallback(async (skipBudgetFilter = false) => {
    // If we have active filters, load more raw data for filtering
    if (Object.keys(filters).length > 0) {
      return loadMoreDataForFiltering();
    }
    
    // Otherwise, use the original logic
    if (!paginationInfo || !paginationInfo.hasNextPage || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      let queryFilters = { ...filters, page: paginationInfo.currentPage + 1, limit: pageSize };
      
      // Same logic as loadPage - only apply budget filter if no user date filters
      if (currentBudget && !skipBudgetFilter && !filters.startDate && !filters.endDate) {
        const budgetDate = new Date(currentBudget.month);
        const year = budgetDate.getFullYear();
        const month = budgetDate.getMonth();
        
        const startOfMonth = new Date(year, month, 1);
        startOfMonth.setUTCHours(0, 0, 0, 0);
        
        const endOfMonth = new Date(year, month + 1, 0);
        endOfMonth.setUTCHours(23, 59, 59, 999);
        
        queryFilters = {
          ...queryFilters,
          startDate: startOfMonth.toISOString(),
          endDate: endOfMonth.toISOString()
        };
      }

      const response = await transactions.getAll(queryFilters);
      setCurrentTransactions(prev => [...prev, ...response.transactions]);
      setPaginationInfo(response.pagination);
    } catch (err) {
      console.error('Error loading more transactions:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [filters, pageSize, currentBudget, paginationInfo, loadingMore, loadMoreDataForFiltering]);

  // Function to get all transactions for filtering
  const getAllTransactions = useCallback(async (skipBudgetFilter = false) => {
    try {
      let queryFilters = { ...filters, getAllForFilters: true };
      
      // Same logic as loadPage - only apply budget filter if no user date filters
      if (currentBudget && !skipBudgetFilter && !filters.startDate && !filters.endDate) {
        const budgetDate = new Date(currentBudget.month);
        const year = budgetDate.getFullYear();
        const month = budgetDate.getMonth();
        
        const startOfMonth = new Date(year, month, 1);
        startOfMonth.setUTCHours(0, 0, 0, 0);
        
        const endOfMonth = new Date(year, month + 1, 0);
        endOfMonth.setUTCHours(23, 59, 59, 999);
        
        queryFilters = {
          ...queryFilters,
          startDate: startOfMonth.toISOString(),
          endDate: endOfMonth.toISOString()
        };
      }

      const response = await transactions.getAll(queryFilters);
      setAllTransactions(response.transactions);
    } catch (err) {
      console.error('Error getting all transactions:', err);
    }
  }, [filters, currentBudget]);

  // Function to refresh transactions
  const refreshTransactions = useCallback(async (skipBudgetFilter = false) => {
    // Reset the lastLoadParams to allow fresh load
    lastLoadParams.current = '';
    await loadPage(1, skipBudgetFilter);
  }, [loadPage]);

  // Add refs to prevent duplicate API calls
  const loadInProgress = useRef(false);
  const lastLoadParams = useRef<string>('');

  // Load first page when budget changes (for dashboard) or page size changes
  useEffect(() => {
    // Only auto-load with budget filter if we have a budget (dashboard context)
    // Don't auto-load for transactions page - let the component handle its own loading
    if (currentBudget && window.location.pathname === '/') {
      // Create a unique key for this load request
      const loadKey = `${currentBudget._id}-${pageSize}-dashboard`;
      
      // Prevent duplicate calls
      if (loadInProgress.current || lastLoadParams.current === loadKey) {
        return;
      }
      
      lastLoadParams.current = loadKey;
      loadPage(1); // Only auto-load on dashboard
    }
  }, [currentBudget, pageSize]); // Remove loadPage from dependencies to prevent loop

  // Listen for undo/redo events to refresh transactions
  useEffect(() => {
    const handleTransactionUndoRedo = () => {
      // Only refresh if we're on the dashboard (where recent transactions are shown)
      if (window.location.pathname === '/') {
        refreshTransactions();
      }
    };

    window.addEventListener('transactionUndoRedo', handleTransactionUndoRedo);
    
    return () => {
      window.removeEventListener('transactionUndoRedo', handleTransactionUndoRedo);
    };
  }, []); // Remove refreshTransactions dependency to prevent recreation

  // Function to handle filter changes
  const handleSetFilters = useCallback((newFilters: TransactionFilters) => {
    setFilters(newFilters);
    // No need to reset pagination or reload - filtering happens instantly
  }, []);

  // Function to handle page size changes
  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    // Reset pagination when page size changes
    setPaginationInfo(null);
  }, []);

  // Function to update transaction category
  const handleUpdateTransactionCategory = useCallback(async (transaction: Transaction, newCategory: string) => {
    if (updatingTransaction) return;
    
    try {
      setUpdatingTransaction(true);
      
      const change: TransactionChange = {
        type: 'transaction_category',
        transactionId: transaction._id,
        oldCategory: transaction.category,
        newCategory: newCategory,
        timestamp: Date.now()
      };

      const updatedTransaction = { ...transaction, category: newCategory };
      
      // Optimistically update UI
      setCurrentTransactions((prev: Transaction[]) => prev.map(t => 
        t._id === transaction._id ? updatedTransaction : t
      ));
      setAllTransactions((prev: Transaction[]) => prev.map(t => 
        t._id === transaction._id ? updatedTransaction : t
      ));

      // Make the API call to update transaction
      await transactions.update(transaction._id, updatedTransaction);

      // Update budget amounts
      if (currentBudget) {
        const updatedBudget = {
          ...currentBudget,
          categories: currentBudget.categories.map(category => {
            if (category.name === transaction.category && category.type === transaction.type) {
              return {
                ...category,
                actualAmount: category.actualAmount - transaction.amount
              };
            }
            if (category.name === newCategory && category.type === transaction.type) {
              return {
                ...category,
                actualAmount: category.actualAmount + transaction.amount
              };
            }
            return category;
          })
        };
        
        setCurrentBudget(updatedBudget);
      }

      // Add to change history
      setChangeHistory(prev => [...prev, change]);
      setUndoneChanges([]);
    } catch (err) {
      // Revert changes on error
      setCurrentTransactions((prev: Transaction[]) => prev.map(t => 
        t._id === transaction._id ? transaction : t
      ));
      setAllTransactions((prev: Transaction[]) => prev.map(t => 
        t._id === transaction._id ? transaction : t
      ));
      console.error('Error updating transaction:', err);
    } finally {
      setUpdatingTransaction(false);
    }
  }, [updatingTransaction, setChangeHistory, setUndoneChanges, currentBudget, setCurrentBudget]);

  const value = React.useMemo(() => ({
    transactions: filteredCurrentTransactions, // Return filtered transactions
    recentTransactions,
    pagination: paginationInfo,
    loading,
    loadingMore,
    filters,
    setFilters: handleSetFilters,
    categoryFilter,
    setCategoryFilter,
    pageSize,
    setPageSize: handleSetPageSize,
    loadPage,
    loadMoreTransactions,
    handleUpdateTransactionCategory,
    refreshTransactions,
    allTransactions,
    getAllTransactions
  }), [
    filteredCurrentTransactions, // Updated dependency
    recentTransactions,
    paginationInfo,
    loading,
    loadingMore,
    filters,
    handleSetFilters,
    categoryFilter,
    setCategoryFilter,
    pageSize,
    handleSetPageSize,
    loadPage,
    loadMoreTransactions,
    handleUpdateTransactionCategory,
    refreshTransactions,
    allTransactions,
    getAllTransactions
  ]);

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
}; 